import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  Wrench, Users, Clock, FileText, Package, Calendar,
  MessageCircle, BarChart3, Shield, Smartphone, Mail,
  Star, Zap, CheckCircle, ArrowRight,
  Car, ClipboardList, Bell, TrendingUp, QrCode,
  Play, Volume2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────── HELPERS ─────────────── */
const Section = ({ children, className = '', dark = true, id }) => (
  <section id={id} className={`relative ${dark ? 'bg-[#0A0A0A] text-white' : 'bg-[#FAFAFA] text-[#0F172A]'} ${className}`}>
    <div className="relative z-10 px-6 md:px-12 lg:px-24 py-20 md:py-32">{children}</div>
  </section>
);

const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Overline = ({ children, dark = true }) => (
  <span
    className={`font-mono text-xs tracking-[0.25em] uppercase ${dark ? 'text-[#E70000]' : 'text-[#E70000]'} block mb-4`}
    style={{ fontFamily: "'JetBrains Mono', monospace" }}
  >
    {children}
  </span>
);

/* ─────────────── HERO ─────────────── */
const Hero = ({ isPlaying }) => {
  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 0.3], ['0%', '15%']);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.3]);

  return (
    <section className="relative h-screen min-h-[700px] flex items-center overflow-hidden bg-[#0A0A0A]">
      {/* Background image */}
      <motion.div className="absolute inset-0" style={{ y: bgY, opacity }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1635108198165-1773945e506e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMGF1dG8lMjByZXBhaXIlMjBnYXJhZ2UlMjBpbnRlcmlvciUyMGx1eHVyeXxlbnwwfHx8fDE3NzM4NTEzMTZ8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-black/85" />
      </motion.div>

      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />

      {/* Red accent line */}
      <div className="absolute top-0 left-0 w-full h-[2px] bg-[#E70000]" />

      {/* Audio pulse effect */}
      <AnimatePresence>
        {isPlaying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-0 left-0 right-0 h-1 z-20 overflow-hidden"
          >
            <motion.div
              className="h-full bg-[#E70000]"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ width: '30%' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-6xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Overline>Logiciel de gestion tout-en-un</Overline>
        </motion.div>

        <div className="overflow-hidden">
          <motion.h1
            className="text-5xl sm:text-6xl lg:text-8xl font-black text-white leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            VOTRE GARAGE.
          </motion.h1>
        </div>
        <div className="overflow-hidden">
          <motion.h1
            className="text-5xl sm:text-6xl lg:text-8xl font-black leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#E70000' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            DIGITALISE.
          </motion.h1>
        </div>

        <motion.p
          className="mt-8 text-lg md:text-xl text-[#A1A1AA] max-w-xl leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
        >
          Bien plus qu'un logiciel. Un ecosysteme vivant qui grandit avec vous.
          Fiches de travail, facturation, marketplace, tombolas, encheres.
          Seul, vous etes un garage. Ensemble, vous etes une force.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
        >
          <a
            href="#contact"
            className="inline-flex items-center justify-center bg-[#E70000] hover:bg-[#C20000] text-white font-bold tracking-widest uppercase text-sm px-8 py-4 transition-all duration-300 group"
            data-testid="hero-cta-btn"
          >
            Nous contacter
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#fonctionnalites"
            className="inline-flex items-center justify-center border border-white/20 hover:bg-white hover:text-black text-white bg-transparent font-bold tracking-widest uppercase text-sm px-8 py-4 transition-all duration-300"
            data-testid="hero-features-btn"
          >
            Decouvrir
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-[1px] h-8 bg-gradient-to-b from-transparent to-white/30" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Scroll</span>
      </motion.div>
    </section>
  );
};

/* ─────────────── PROBLEM / SOLUTION ─────────────── */
const ProblemSolution = () => (
  <Section dark={false} id="probleme">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <Overline dark={false}>Le constat</Overline>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          La realite du terrain.
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-12 mt-12">
        <Reveal delay={0.1}>
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-[#64748B] uppercase tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>Sans Vortekio</h3>
            {[
              'Des fiches de travail qui s\'accumulent sur le bureau',
              'Des rendez-vous oublies, des clients mecontents',
              'La facturation le soir et les week-ends',
              'Des pieces introuvables dans le stock',
              'Aucune visibilite sur la rentabilite',
              'Du temps perdu au lieu de reparer des voitures',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E70000] mt-2 shrink-0" />
                <p className="text-[#475569]" style={{ fontFamily: "'Manrope', sans-serif" }}>{item}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-[#059669] uppercase tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>Avec Vortekio</h3>
            {[
              'Tout centralise dans une seule plateforme intuitive',
              'Fiches de travail numeriques avec signature electronique',
              'Facturation suisse automatique avec QR-facture integre',
              'Relances clients automatisees par WhatsApp et email',
              'Tableau de bord financier en temps reel',
              'Plus de temps pour vos clients, plus de rentabilite',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 shrink-0" />
                <p className="text-[#334155] font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>{item}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </Section>
);

/* ─────────────── FEATURES BENTO GRID ─────────────── */
const features = [
  { icon: ClipboardList, title: 'Fiches de travail', desc: 'Creez, suivez et facturez chaque intervention. Signature electronique par WhatsApp. Tracabilite totale.', span: 'md:col-span-8' },
  { icon: Users, title: 'CRM Clients', desc: 'Base clients complete avec historique vehicules, relances automatiques et suivi de satisfaction.', span: 'md:col-span-4' },
  { icon: QrCode, title: 'Pointage QR', desc: 'Vos employes pointent avec un QR code. Precis, anti-fraude, sans papier.', span: 'md:col-span-4' },
  { icon: FileText, title: 'Facturation suisse', desc: 'Factures avec QR-facture integre, devis et bons de commande conformes aux normes suisses.', span: 'md:col-span-4' },
  { icon: Calendar, title: 'Agenda intelligent', desc: 'Rendez-vous, rappels automatiques par WhatsApp. Vos clients n\'oublient plus jamais.', span: 'md:col-span-4' },
  { icon: MessageCircle, title: 'WhatsApp Business', desc: 'Devis, travaux supplementaires, factures : tout passe par WhatsApp. Le client repond en un clic.', span: 'md:col-span-6' },
  { icon: Bell, title: 'Notifications auto', desc: 'Vehicule pret, confirmation RDV, rappels. Tout est automatise. Zero effort.', span: 'md:col-span-6' },
  { icon: Car, title: 'Marketplace VO', desc: 'Achetez et vendez vehicules, pieces et materiel entre garagistes. Fini les intermediaires.', span: 'md:col-span-4' },
  { icon: Shield, title: 'Comptabilite complete', desc: 'Comptabilite Vortekio integree, fiches de salaire automatisees, exports conformes.', span: 'md:col-span-4' },
  { icon: BarChart3, title: 'Dashboard & IA', desc: 'Tableau de bord avec insights IA, rapports mensuels automatiques, KPIs en temps reel.', span: 'md:col-span-4' },
];

const FeatureCard = ({ icon: Icon, title, desc, span, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      className={`${span} bg-[#111111] border border-[#27272A] p-8 group hover:border-[#E70000] transition-all duration-300`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ y: -4 }}
    >
      <div className="w-10 h-10 flex items-center justify-center border border-[#27272A] group-hover:border-[#E70000] group-hover:bg-[#E70000]/10 transition-all duration-300 mb-6">
        <Icon className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#E70000] transition-colors duration-300" />
      </div>
      <h3
        className="text-lg font-bold text-white mb-3 tracking-tight"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        {title}
      </h3>
      <p className="text-sm text-[#A1A1AA] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{desc}</p>
    </motion.div>
  );
};

const Features = () => (
  <Section id="fonctionnalites">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <Overline>Fonctionnalites</Overline>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          TOUT CE DONT VOTRE GARAGE A BESOIN
        </h2>
        <p className="text-[#A1A1AA] max-w-2xl text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>
          10 modules interconnectes pour piloter votre activite de A a Z.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-16">
        {features.map((f, i) => (
          <FeatureCard key={i} {...f} index={i} />
        ))}
      </div>
    </div>
  </Section>
);

/* ─────────────── HOW IT WORKS ─────────────── */
const steps = [
  { num: '01', title: 'Configurez', desc: 'Importez vos clients, definissez vos taux horaires et parametrez votre equipe. En quelques minutes, c\'est pret.' },
  { num: '02', title: 'Gerez', desc: 'Fiches de travail, signatures, facturation, relances. Tout se fait en 2-3 clics, depuis une seule interface.' },
  { num: '03', title: 'Liberez-vous', desc: 'Fini les week-ends a facturer. Fini les soirees a preparer les rendez-vous. Recuperez votre temps.' },
];

const HowItWorks = () => (
  <Section dark={false} id="fonctionnement">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <Overline dark={false}>Comment ca marche</Overline>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          3 ETAPES. C'EST TOUT.
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-8 mt-16">
        {steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.15}>
            <div className="relative">
              <span
                className="text-8xl font-black text-[#E70000]/10 absolute -top-6 -left-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {s.num}
              </span>
              <div className="relative pt-12">
                <h3
                  className="text-2xl font-bold tracking-tight mb-4"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {s.title}
                </h3>
                <p className="text-[#475569] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ─────────────── STATS ─────────────── */
const StatCounter = ({ value, suffix = '', label }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  return (
    <div ref={ref} className="text-center">
      <motion.span
        className="text-5xl md:text-6xl font-black text-white block"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5, type: 'spring' }}
      >
        {value}{suffix}
      </motion.span>
      <span className="text-[#A1A1AA] text-sm mt-2 block tracking-wide uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
    </div>
  );
};

const Stats = () => (
  <section className="relative bg-[#0A0A0A] border-y border-[#27272A]">
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        <StatCounter value="200" suffix="+" label="Garages en Suisse" />
        <StatCounter value="14" suffix=" j" label="Essai gratuit" />
        <StatCounter value="100" suffix="%" label="Swiss-made" />
        <StatCounter value="24" suffix="/7" label="Accessible partout" />
      </div>
    </div>
  </section>
);

/* ─────────────── TRUST / SWISS QUALITY ─────────────── */
const Trust = () => (
  <Section id="confiance">
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Overline>Pourquoi Vortekio</Overline>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            SEUL, VOUS ETES UN GARAGE.<br />
            <span className="text-[#E70000]">ENSEMBLE, VOUS ETES UNE FORCE.</span>
          </h2>
          <div className="space-y-6 mt-8">
            {[
              { icon: Zap, title: 'Toujours a la pointe', desc: 'Des mises a jour regulieres, des nouvelles fonctionnalites, des alertes flash. Votre outil evolue en permanence.' },
              { icon: TrendingUp, title: 'Un reseau qui grandit', desc: 'Marketplace, encheres inversees, tombolas, evenements. Des plateformes qui se rajoutent pour connecter les garagistes entre eux.' },
              { icon: Wrench, title: 'Ne du terrain', desc: 'Chaque fonctionnalite repond a un vrai probleme du quotidien. L\'equipe vit la technologie et repousse les limites chaque jour.' },
              { icon: Smartphone, title: 'Grandir ensemble', desc: 'Chaque garage grandit individuellement avec des outils puissants. Mais ensemble, sur Vortekio, vous formez un vrai reseau.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="flex gap-4">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center border border-[#27272A]">
                    <Icon className="w-5 h-5 text-[#E70000]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{title}</h4>
                    <p className="text-sm text-[#A1A1AA]" style={{ fontFamily: "'Manrope', sans-serif" }}>{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1640627139231-66a056ac3eb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxzd2lzcyUyMGFscHMlMjBtb3VudGFpbnMlMjBsYW5kc2NhcGUlMjBtaW5pbWFsfGVufDB8fHx8MTc3Mzg1MTMxOHww&ixlib=rb-4.1.0&q=85"
                alt="Swiss Alps"
                className="w-full h-full object-cover opacity-60"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-[#E70000]" />
                <span className="text-sm font-mono tracking-wider text-white/80" style={{ fontFamily: "'JetBrains Mono', monospace" }}>CREE EN SUISSE ROMANDE</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </Section>
);

/* ─────────────── DEMO MOCKUP ─────────────── */
const DemoMockup = () => (
  <Section dark={false} id="apercu">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <div className="text-center mb-16">
          <Overline dark={false}>Apercu</Overline>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            UNE INTERFACE QUI VA A L'ESSENTIEL
          </h2>
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="relative">
          {/* Browser mockup */}
          <div className="bg-[#111111] rounded-sm border border-[#27272A] overflow-hidden shadow-2xl">
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#27272A]">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#E70000]/60" />
                <div className="w-3 h-3 rounded-full bg-[#F59E0B]/60" />
                <div className="w-3 h-3 rounded-full bg-[#22C55E]/60" />
              </div>
              <div className="flex-1 ml-4">
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm px-4 py-1.5 text-xs text-[#A1A1AA] font-mono max-w-md" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  app.vortekio.ch/admin/dashboard
                </div>
              </div>
            </div>
            {/* Dashboard mockup content */}
            <div className="p-6 bg-[#0F172A]">
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Fiches en cours', val: '12', color: '#3B82F6' },
                  { label: 'RDV aujourd\'hui', val: '5', color: '#22C55E' },
                  { label: 'Factures impayees', val: '3', color: '#F59E0B' },
                  { label: 'Productivite', val: '87%', color: '#E70000' },
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    className="bg-[#1E293B] border border-[#334155] rounded p-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                  >
                    <p className="text-xs text-[#94A3B8] mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{card.label}</p>
                    <p className="text-2xl font-bold" style={{ color: card.color, fontFamily: "'Barlow Condensed', sans-serif" }}>{card.val}</p>
                  </motion.div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 bg-[#1E293B] border border-[#334155] rounded p-4 h-40">
                  <p className="text-xs text-[#94A3B8] mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>Planning atelier</p>
                  <div className="space-y-2">
                    {['Service complet - BMW 320d', 'Freins AV - Audi A4', 'Diagnostic - VW Golf'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ['#3B82F6', '#22C55E', '#F59E0B'][i] }} />
                        <span className="text-xs text-[#CBD5E1]" style={{ fontFamily: "'Manrope', sans-serif" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#1E293B] border border-[#334155] rounded p-4 h-40">
                  <p className="text-xs text-[#94A3B8] mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>Activite equipe</p>
                  <div className="space-y-3">
                    {['Sebastiano', 'Marco', 'Luca'].map((name, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs text-[#CBD5E1]" style={{ fontFamily: "'Manrope', sans-serif" }}>{name}</span>
                        <div className="w-16 h-1.5 bg-[#0F172A] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${[85, 72, 91][i]}%`, backgroundColor: '#E70000' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ─────────────── MODULES LIST ─────────────── */
const modules = [
  { icon: Star, name: 'Satisfaction client', desc: 'Enquetes automatiques apres intervention' },
  { icon: Clock, name: 'Suivi temps', desc: 'Chrono par intervention, rapport productivite' },
  { icon: Wrench, name: 'Atelier connecte', desc: 'Dashboard mecanicien, liste de taches, photos' },
  { icon: Users, name: 'Communication interne', desc: 'Echanges entre equipes en temps reel' },
];

const ModulesList = () => (
  <Section>
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <Overline>Et aussi</Overline>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-16"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          TOUT A ETE PENSE
        </h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A]">
        {modules.map(({ icon: Icon, name, desc }, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="bg-[#0A0A0A] p-8 h-full hover:bg-[#111111] transition-colors duration-300 group">
              <Icon className="w-6 h-6 text-[#A1A1AA] group-hover:text-[#E70000] transition-colors duration-300 mb-6" />
              <h4 className="font-bold text-white mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{name}</h4>
              <p className="text-xs text-[#A1A1AA]" style={{ fontFamily: "'Manrope', sans-serif" }}>{desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ─────────────── VISION / CE QUI ARRIVE ─────────────── */
const Vision = () => (
  <Section id="vision">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <Overline>L'ecosysteme Vortekio</Overline>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          AUJOURD'HUI UN LOGICIEL.<br />
          <span className="text-[#E70000]">DEMAIN, UNE REVOLUTION.</span>
        </h2>
        <p className="text-[#A1A1AA] text-base max-w-2xl mb-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Vortekio grandit avec vous. De nouvelles plateformes, de nouvelles experiences,
          de nouvelles opportunites arrivent regulierement. Voici ce qui vous attend.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { icon: '⚡', title: 'Alertes Flash', desc: 'Promotions, nouvelles fonctionnalites, mises a jour. Recevez tout en temps reel sur votre tableau de bord.' },
          { icon: '🔄', title: 'Encheres inversees', desc: 'Publiez vos besoins d\'achat vehicules. Les vendeurs font baisser le prix pour vous. Vous choisissez la meilleure offre.', image: 'https://static.prod-images.emergentagent.com/jobs/21d05c5c-9f83-40e6-93ab-1f1565c6a6be/images/02be5a1c32fb9ea66ed499d7ce6f70c455143b7e4461e1821a6f43af32dd1f5f.png' },
          { icon: '🎰', title: 'Tombolas & Evenements', desc: 'Organisez des tombolas pour vos clients, ou participez a celles du reseau Vortekio. Fidelisez autrement.', image: 'https://static.prod-images.emergentagent.com/jobs/21d05c5c-9f83-40e6-93ab-1f1565c6a6be/images/29d11fe6d443b63cd201a8ab711ec9ba7f6be04ae8be7c6e8a73fe9d6366dacf.png' },
          { icon: '📲', title: 'Nouvelles applis', desc: 'Des applications qui se rajoutent regulierement. Scan code-barres, suivi GPS, et bien plus encore.' },
          { icon: '🤝', title: 'Reseau garagistes', desc: 'Achetez, vendez, echangez entre professionnels. Un vrai reseau de confiance entre garagistes suisses.' },
          { icon: '🚀', title: 'Innovation continue', desc: 'L\'equipe derriere Vortekio repousse les limites chaque jour. Nouvelles technologies, nouvelles experiences, toujours.' },
        ].map(({ icon, title, desc, image }, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="bg-[#111111] border border-[#27272A] h-full hover:border-[#E70000]/30 transition-all duration-300 group overflow-hidden">
              {image && (
                <div className="h-40 overflow-hidden">
                  <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100" />
                </div>
              )}
              <div className="p-8">
                {!image && <span className="text-3xl mb-4 block">{icon}</span>}
                <h4 className="font-bold text-white text-lg mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{title}</h4>
                <p className="text-sm text-[#A1A1AA] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.5}>
        <div className="mt-12 text-center">
          <p className="text-[#E70000] font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            VORTEKIO GRANDIT AVEC VOUS. VOUS GRANDISSEZ AVEC VORTEKIO.
          </p>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ─────────────── CONTACT CTA ─────────────── */
const Contact = () => (
  <Section id="contact">
    <div className="max-w-4xl mx-auto text-center">
      <Reveal>
        <Overline>Pret a rejoindre le reseau ?</Overline>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          CONTACTEZ-NOUS
        </h2>
        <p className="text-[#A1A1AA] text-lg mb-12 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Quatorze jours d'essai gratuit. Acces complet. Sans engagement.
          Decouvrez pourquoi plus de 200 garages en Suisse nous font confiance.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <a
          href="mailto:info@vortekio.ch"
          className="inline-flex items-center justify-center bg-[#E70000] hover:bg-[#C20000] text-white font-bold tracking-widest uppercase text-sm px-12 py-5 transition-all duration-300 group"
          data-testid="contact-email-btn"
        >
          <Mail className="w-5 h-5 mr-3" />
          info@vortekio.ch
          <ArrowRight className="ml-3 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
      </Reveal>
      <Reveal delay={0.3}>
        <p className="mt-8 text-xs text-[#A1A1AA] tracking-wider uppercase font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Reponse sous 24h
        </p>
      </Reveal>
    </div>
  </Section>
);

/* ─────────────── FOOTER ─────────────── */
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-[#27272A]">
    <div className="px-6 md:px-12 lg:px-24 py-8">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-[#E70000]" />
          <span className="text-sm font-bold tracking-widest text-white uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Vortekio</span>
        </div>
        <nav className="flex gap-8">
          {[
            { label: 'Fonctionnalites', href: '#fonctionnalites' },
            { label: 'Confiance', href: '#confiance' },
            { label: 'Contact', href: '#contact' },
          ].map(({ label, href }) => (
            <a key={label} href={href} className="text-xs text-[#A1A1AA] hover:text-white transition-colors tracking-wider uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {label}
            </a>
          ))}
        </nav>
        <p className="text-xs text-[#64748B]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {new Date().getFullYear()} Vortekio. Swiss-made.
        </p>
      </div>
    </div>
  </footer>
);

/* ─────────────── VOICEOVER PLAYER ─────────────── */
const voiceoverTimeline = [
  { time: 0, section: 'hero' },
  { time: 8, section: 'hero' },
  { time: 14, section: 'probleme' },
  { time: 22, section: 'probleme' },
  { time: 32, section: 'fonctionnalites' },
  { time: 40, section: 'fonctionnalites' },
  { time: 52, section: 'apercu' },
  { time: 60, section: 'modules' },
  { time: 68, section: 'vision' },
  { time: 78, section: 'vision' },
  { time: 88, section: 'confiance' },
  { time: 95, section: 'confiance' },
  { time: 102, section: 'contact' },
];

/* ─────────────── INTRO SPLASH ─────────────── */
const IntroSplash = ({ onStart }) => (
  <motion.div
    className="fixed inset-0 z-[100] bg-[#0A0A0A] flex items-center justify-center"
    exit={{ opacity: 0 }}
    transition={{ duration: 0.8 }}
  >
    {/* Noise texture */}
    <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />

    <div className="relative text-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mb-8"
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-3 h-3 bg-[#E70000]" />
          <span className="text-2xl font-bold tracking-[0.3em] text-white uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Vortekio</span>
        </div>
        <p className="text-[#A1A1AA] text-sm tracking-wider uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Logiciel de gestion pour garages
        </p>
      </motion.div>

      <motion.button
        onClick={onStart}
        className="relative group"
        data-testid="start-presentation-btn"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Pulse rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full border border-[#E70000]/20 animate-ping" style={{ animationDuration: '2s' }} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-32 rounded-full border border-[#E70000]/10 animate-ping" style={{ animationDuration: '3s' }} />
        </div>

        <div className="relative w-20 h-20 flex items-center justify-center bg-[#E70000] hover:bg-[#C20000] transition-colors duration-300">
          <Play className="w-8 h-8 text-white ml-1" />
        </div>
      </motion.button>

      <motion.p
        className="mt-8 text-xs text-[#64748B] tracking-wider uppercase"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        Cliquez pour lancer la presentation
      </motion.p>
    </div>
  </motion.div>
);

/* ─────────────── NAVBAR ─────────────── */
const Navbar = ({ isPlaying }) => {
  const [scrolled, setScrolled] = React.useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[#27272A]' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-[#E70000]" />
          <span className="text-sm font-bold tracking-widest text-white uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Vortekio</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'Fonctionnalites', href: '#fonctionnalites' },
            { label: 'Apercu', href: '#apercu' },
            { label: 'Confiance', href: '#confiance' },
          ].map(({ label, href }) => (
            <a key={label} href={href} className="text-xs text-[#A1A1AA] hover:text-white transition-colors tracking-wider uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-4">
          {isPlaying && (
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-[#E70000] animate-pulse" />
              <span className="text-xs text-[#E70000] tracking-wider uppercase hidden md:block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>En lecture</span>
            </div>
          )}
          <a
            href="#contact"
            className="text-xs bg-[#E70000] hover:bg-[#C20000] text-white font-bold tracking-widest uppercase px-6 py-2.5 transition-all duration-300"
            data-testid="nav-contact-btn"
          >
            Contact
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

/* ─────────────── MAIN PAGE ─────────────── */
const PresentationPage = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const lastSectionRef = useRef('');

  useEffect(() => {
    document.title = 'Vortekio - Logiciel de gestion pour garages';
  }, []);

  const startPresentation = useCallback(() => {
    setShowSplash(false);

    // Small delay to let the page render, then start audio + scroll
    setTimeout(() => {
      const audio = audioRef.current;
      if (audio) {
        audio.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {
          // Fallback: still auto-scroll even without audio
          setIsPlaying(true);
        });
      }
    }, 800);
  }, []);

  // Auto-scroll synced with audio
  useEffect(() => {
    if (!isPlaying) {
      clearInterval(scrollIntervalRef.current);
      return;
    }

    scrollIntervalRef.current = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const t = audio.currentTime;

      // Find current section
      let targetSection = 'hero';
      for (let i = voiceoverTimeline.length - 1; i >= 0; i--) {
        if (t >= voiceoverTimeline[i].time) {
          targetSection = voiceoverTimeline[i].section;
          break;
        }
      }

      // Only scroll if section changed
      if (targetSection !== lastSectionRef.current) {
        lastSectionRef.current = targetSection;
        const el = document.getElementById(targetSection);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 400);

    return () => clearInterval(scrollIntervalRef.current);
  }, [isPlaying]);

  // Handle audio end
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <audio ref={audioRef} src={`${API}/api/uploads/vortekio_voiceover.mp3`} preload="auto" />

      <AnimatePresence>
        {showSplash && <IntroSplash onStart={startPresentation} />}
      </AnimatePresence>

      <Navbar isPlaying={isPlaying} />
      <Hero isPlaying={isPlaying} />
      <ProblemSolution />
      <Stats />
      <Features />
      <DemoMockup />
      <HowItWorks />
      <ModulesList />
      <Vision />
      <Trust />
      <Contact />
      <Footer />
    </div>
  );
};

export default PresentationPage;
